import { Close, Message, Phone } from "@mui/icons-material";
import AssistantIcon from "@mui/icons-material/Assistant";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PeopleIcon from "@mui/icons-material/People";
import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { deepOrange } from "@mui/material/colors";
import { blueGrey } from "../../../theme/muiColors";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { useFloatingBubbles } from "../../../context/floating-bubbles/FloatingBubblesContext";
import { useUnreadMessagesContext } from "../../../context/unread-messages/UnreadMessagesContext";
import { MessagesView } from "../../Messages/view/MessageView";
import { PortingOrchestrator } from "../../amazon-connect/porting/orchestrators/PortingOrchestrator";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { colors } from "../../ui/colors";
import { useInternalContacts } from "../_hooks/useInternalContacts";
import { useContactPersonStatus } from "../_hooks/useContactPersonStatus";
import { Contact } from "../_types/internalContacts.types";
import { getContactId, getContactTitle, ORGANIZATION_CONTACT_ROLES } from "../_utils/internalContacts.utils";
import { filterEmployeeCompanyContacts } from "../_utils/employeeContacts.utils";
import { isCallableContact } from "../_utils/callRoom.utils";
import { InternalContactsCallView } from "./InternalContactsCallView";
import { InternalContactsList } from "./InternalContactsList";
import { InternalContactsSectionHeader } from "./InternalContactsSectionHeader";
import { useInternalWebRTC } from "../_context/InternalWebRTCContext";
import { useInternalTwilioCall } from "../../internal-twilio-call/context/InternalTwilioCallContext";
import { useStartInternalTwilioPersonCall } from "../../internal-twilio-call/hooks/useStartInternalTwilioPersonCall";
import { InternalTwilioCallView } from "../../internal-twilio-call/components/InternalTwilioCallView";

const BLUE_BASE = colors.info.base || "#2196F3";

export const InternalContactsView = () => {
  const { uid, company, roles } = useSelector((state: any) => state.userState);
  const role = company?.role?.type ?? roles?.[0]?.type;

  const { messagesOpen, activeBubble, anyBubbleOpen, toggleMessages, closeMessages, openCalls, zIndex } = useFloatingBubbles();

  const isActive = activeBubble === "messages";

  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [activeCallContact, setActiveCallContact] = useState<Contact | null>(null);
  const [callInProgress, setCallInProgress] = useState(false);
  const [callingContactId, setCallingContactId] = useState<string | null>(null);
  const [hasNewMessage, setHasNewMessage] = useState(false);

  const { internalCallView } = useInternalWebRTC();
  const internalTwilioCall = useInternalTwilioCall();
  const startTwilioPersonCall = useStartInternalTwilioPersonCall();
  const isTwilioCallActive = internalTwilioCall.status !== "idle";

  const { contacts: allContacts, refetch } = useInternalContacts();
  const { getUnreadCount, getTotalUnreadCount, setActiveContact } = useUnreadMessagesContext();

  const totalUnreadCount = getTotalUnreadCount();

  const bots = useMemo(() => allContacts.filter((c: Contact) => c.isBot), [allContacts]);
  const amazonConnect = useMemo(() => allContacts.filter((c: Contact) => c.isAmazonConnect), [allContacts]);
  const peopleContacts = useMemo(() => allContacts.filter((c: Contact) => !c.isBot && !c.isAmazonConnect), [allContacts]);
  const workforceContacts = useMemo(() => peopleContacts.filter((c: Contact) => c.type === "workforce"), [peopleContacts]);
  const organizationCoworkerContacts = useMemo(() => peopleContacts.filter((c: Contact) => c.type === "organizationCoworker"), [peopleContacts]);

  const isOrganizationMember = ORGANIZATION_CONTACT_ROLES.includes(role);
  const isEmployee = role === "employee";
  const employeeCompanyContacts = useMemo(() => filterEmployeeCompanyContacts(peopleContacts, company?.id), [peopleContacts, company?.id]);
  const { resolvePersonStatus } = useContactPersonStatus(isOrganizationMember || isEmployee);

  const headerTitle = activeCallContact
    ? `Call · ${getContactTitle(activeCallContact)}`
    : isTwilioCallActive
      ? `Call · ${internalTwilioCall.remoteName || "Mobile contact"}`
      : selectedContact
        ? getContactTitle(selectedContact)
        : "Internal Contacts";

  const handleContactSelect = useCallback(
    (contact: Contact) => {
      setActiveCallContact(null);
      setSelectedContact(contact);
      if (!contact.isTwilio) setActiveContact(contact);
    },
    [setActiveContact],
  );

  const handleStartCall = useCallback(async () => {
    if (!isCallableContact(selectedContact) || callInProgress) return;

    setCallInProgress(true);
    try {
      const started = await startTwilioPersonCall(selectedContact, "InternalContactsView.start");
      if (!started) return;
      openCalls();
    } finally {
      setCallInProgress(false);
    }
  }, [callInProgress, openCalls, selectedContact, startTwilioPersonCall]);

  const handleContactCall = useCallback(
    async (contact: Contact) => {
      if (!isCallableContact(contact) || callingContactId || internalCallView || isTwilioCallActive) {
        return;
      }

      const contactId = String(getContactId(contact));
      setCallingContactId(contactId);

      try {
        const started = await startTwilioPersonCall(contact, "InternalContactsView.item");
        if (!started) return;
        openCalls();
      } finally {
        setCallingContactId(null);
      }
    },
    [callingContactId, internalCallView, isTwilioCallActive, openCalls, startTwilioPersonCall],
  );

  const handleEndCall = useCallback(() => {
    setActiveCallContact(null);
  }, []);

  const handleToolbarClose = useCallback(() => {
    if (activeCallContact) {
      handleEndCall();
      return;
    }
    if (selectedContact) {
      setSelectedContact(null);
      return;
    }
    closeMessages();
  }, [activeCallContact, selectedContact, handleEndCall, closeMessages]);

  useEffect(() => {
    if (internalTwilioCall.status === "ringing-incoming") {
      openCalls();
    }
  }, [internalTwilioCall.status, openCalls]);

  useEffect(() => {
    if (messagesOpen && role) refetch();
  }, [messagesOpen, role, refetch]);

  useEffect(() => {
    if (totalUnreadCount > 0 && !messagesOpen) {
      setHasNewMessage(true);
      const timer = setTimeout(() => setHasNewMessage(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [totalUnreadCount, messagesOpen]);

  useEffect(() => {
    if (!messagesOpen) {
      setSelectedContact(null);
      setActiveCallContact(null);
      setActiveContact(null);
    }
  }, [messagesOpen, setActiveContact]);

  const token = sessionStorage.getItem("token");
  if (!token || !uid) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: isActive ? zIndex.active : zIndex.inactive,
      }}
    >
      <Tooltip
        title={totalUnreadCount > 0 ? `${totalUnreadCount} unread message${totalUnreadCount === 1 ? "" : "s"}` : "Internal Contacts"}
        PopperProps={{
          sx: { zIndex: zIndex.active + 1 },
        }}
      >
        <IconButton
          onClick={toggleMessages}
          sx={{
            position: "relative",
            width: 56,
            height: 56,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
            animation: hasNewMessage ? "icPulse 2.8s ease-in-out infinite, icSoftShake 1.8s ease-in-out infinite" : "none",
            opacity: anyBubbleOpen && !isActive ? 0.85 : 1,
            transition: "transform 0.2s ease, background-color 0.2s ease",

            "@keyframes icPulse": {
              "0%": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 0 0 0 rgba(33,150,243,0.35)",
              },
              "70%": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 0 0 12px rgba(33,150,243,0)",
              },
              "100%": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 0 0 0 rgba(33,150,243,0)",
              },
            },

            "@keyframes icSoftShake": {
              "0%, 100%": {
                transform: "translateX(0) rotate(0deg)",
              },
              "8%": {
                transform: "translateX(-1px) rotate(-2deg)",
              },
              "16%": {
                transform: "translateX(1px) rotate(2deg)",
              },
              "24%": {
                transform: "translateX(-1px) rotate(-1deg)",
              },
              "32%": {
                transform: "translateX(0) rotate(0deg)",
              },
            },

            "&:hover": {
              bgcolor: colors.orange[50],
              transform: "scale(1.1)",
              animation: hasNewMessage ? "icPulse 2.8s ease-in-out infinite" : "none",
            },
          }}
        >
          <Message
            sx={{
              fontSize: 26,
              color: BLUE_BASE,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.35))",
            }}
          />

          {totalUnreadCount > 0 && (
            <Box
              sx={{
                position: "absolute",
                top: 4,
                right: 4,
                minWidth: 18,
                height: 18,
                px: totalUnreadCount > 9 ? 0.45 : 0,
                borderRadius: "999px",
                bgcolor: colors.orange.base || "#E28743",
                color: "#fff",
                border: "2px solid var(--fx-surface-base, #FFFFFF)",
                boxShadow: "0 0 0 2px rgba(226,135,67,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: 800,
                lineHeight: 1,
                animation: "icDotPulse 1.4s ease-in-out infinite",

                "@keyframes icDotPulse": {
                  "0%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                  "50%": {
                    transform: "scale(1.18)",
                    opacity: 0.8,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            >
              {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
            </Box>
          )}
        </IconButton>
      </Tooltip>

      <AnimatePresence>
        {messagesOpen && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ duration: 0.15 }}
            sx={{
              position: "fixed",
              bottom: 10,
              right: 16,
              width: { xs: "calc(95vw - 24px)", sm: 360, md: 420 },
              maxWidth: { xs: 390, sm: 360, md: 480 },
              height: { xs: "calc(90vh - 90px)", sm: 600, md: 640 },
              maxHeight: { xs: "90vh", sm: 600, md: 640 },
              backgroundColor: "background.paper",
              borderRadius: "16px",
              boxShadow: "0 24px 48px rgba(0,0,0,0.16), 0 8px 16px rgba(0,0,0,0.08)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              zIndex: isActive ? zIndex.active : zIndex.inactive,
            }}
          >
            {/* HEADER */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2.5,
                pt: 2.5,
                pb: 2,
              }}
            >
              <Box sx={{ flexShrink: 0, display: "flex" }}>
                <PeopleIcon
                  sx={{
                    fontSize: 24,
                    color: BLUE_BASE,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  flex: 1,
                  fontWeight: 700,
                  fontSize: 15,
                  color: BLUE_BASE,
                  letterSpacing: "-0.01em",
                  noWrap: true,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {headerTitle}
              </Typography>

              <Box display="flex" alignItems="center" gap={0.5}>
                {selectedContact && !activeCallContact && !internalCallView && !isTwilioCallActive && isCallableContact(selectedContact) && (
                  <IconButton
                    onClick={() => void handleStartCall()}
                    disabled={callInProgress}
                    size="small"
                    sx={{
                      color: BLUE_BASE,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Phone sx={{ fontSize: 18 }} />
                  </IconButton>
                )}

                <IconButton
                  size="small"
                  onClick={handleToolbarClose}
                  sx={{
                    color: "text.secondary",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            {/* CONTENT */}
            <Box
              sx={{
                flex: 1,
                overflow: selectedContact?.isTwilio ? "hidden" : "auto",
              }}
            >
              {activeCallContact ? (
                <InternalContactsCallView contact={activeCallContact} onEndCall={handleEndCall} />
              ) : isTwilioCallActive ? (
                <InternalTwilioCallView contactName={internalTwilioCall.remoteName || "Mobile contact"} onEndCall={handleEndCall} />
              ) : selectedContact ? (
                selectedContact.isTwilio ? (
                  <Box sx={{ height: "100%", width: "100%" }} />
                ) : (
                  <MessagesView selectedContact={selectedContact} />
                )
              ) : (
                <Box sx={{ flex: 1, overflowY: "auto" }}>
                  {isOrganizationMember && (
                    <>
                      <InternalContactsList
                        label="Workforces"
                        contacts={workforceContacts}
                        avatarColor={blueGrey[500]}
                        getUnreadCount={getUnreadCount}
                        onSelect={handleContactSelect}
                        onCall={handleContactCall}
                        hideCallButton={Boolean(internalCallView) || isTwilioCallActive}
                        callingContactId={callingContactId}
                        getPersonStatus={resolvePersonStatus}
                      />
                      <InternalContactsList
                        label="Organization Directory"
                        contacts={organizationCoworkerContacts}
                        avatarColor={blueGrey[700]}
                        getUnreadCount={getUnreadCount}
                        onSelect={handleContactSelect}
                        onCall={handleContactCall}
                        hideCallButton={Boolean(internalCallView) || isTwilioCallActive}
                        callingContactId={callingContactId}
                        getPersonStatus={resolvePersonStatus}
                      />
                    </>
                  )}

                  {isEmployee && employeeCompanyContacts.length > 0 && (
                    <InternalContactsList
                      label={employeeCompanyContacts[0]?.businessName ?? "Organization"}
                      contacts={employeeCompanyContacts}
                      avatarColor={blueGrey[700]}
                      getUnreadCount={getUnreadCount}
                      onSelect={handleContactSelect}
                      onCall={handleContactCall}
                      hideCallButton={Boolean(internalCallView) || isTwilioCallActive}
                      callingContactId={callingContactId}
                      getPersonStatus={resolvePersonStatus}
                    />
                  )}

                  {bots.length > 0 && (
                    <>
                      <InternalContactsSectionHeader label="Artificial Intelligence Assistants" />
                      <List disablePadding>
                        {bots.map((contact: Contact) => (
                          <ListItem key={`bot-${getContactId(contact)}`} disablePadding sx={{ px: 2 }}>
                            <ListItemButton
                              onClick={() => handleContactSelect(contact)}
                              sx={{
                                borderRadius: "12px",
                                py: 1.5,
                                px: 2,
                                gap: 1.5,
                                "&:hover": {
                                  backgroundColor: "action.hover",
                                },
                              }}
                            >
                              <ListItemAvatar>
                                <Badge
                                  badgeContent={getUnreadCount(getContactId(contact))}
                                  color="error"
                                  overlap="circular"
                                  anchorOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      fontSize: "10px",
                                      height: "18px",
                                      minWidth: "18px",
                                      fontWeight: 700,
                                      border: "2px solid",
                                      borderColor: "background.paper",
                                    },
                                  }}
                                >
                                  <Avatar sx={{ bgcolor: blueGrey[700] }}>
                                    <AssistantIcon />
                                  </Avatar>
                                </Badge>
                              </ListItemAvatar>
                              <ListItemText
                                primary={
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: 14,
                                    }}
                                  >
                                    {contact.name}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: 12,
                                    }}
                                  >
                                    {contact.email}
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </>
                  )}

                  {amazonConnect.map((contact: Contact) => (
                    <BaseModalComponent
                      key={`amazon-${getContactId(contact)}`}
                      modalLabel="Register amazon connect"
                      modalKey="Dashboard-Messages-Amazon-Connect-Form"
                      defaultSizes={{ width: "600" }}
                      elementTrigger={
                        <ListItem disablePadding sx={{ px: 2 }}>
                          <ListItemButton
                            sx={{
                              borderRadius: "12px",
                              py: 1.5,
                              px: 2,
                              gap: 1.5,
                              "&:hover": {
                                backgroundColor: "action.hover",
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <Badge
                                badgeContent={getUnreadCount(getContactId(contact))}
                                color="error"
                                overlap="circular"
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                                sx={{
                                  "& .MuiBadge-badge": {
                                    fontSize: "10px",
                                    height: "18px",
                                    minWidth: "18px",
                                    fontWeight: 700,
                                    border: "2px solid",
                                    borderColor: "background.paper",
                                  },
                                }}
                              >
                                <Avatar sx={{ bgcolor: deepOrange[600] }}>
                                  <SupportAgentIcon />
                                </Avatar>
                              </Badge>
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography sx={{ fontWeight: 600, fontSize: 14 }}>{contact.name}</Typography>}
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: 12,
                                  }}
                                >
                                  {contact.email}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      }
                    >
                      <PortingOrchestrator />
                    </BaseModalComponent>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};
